import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Reuse the verified single gift offer's exclusions without changing its rules. */
function isExcludedFromRewardQualification(productId) {
  const gifts = globalThis.MDCCartSavingsConfig?.gifts;
  const offer = gifts?.length === 1 && gifts[0].rulesVerified && gifts[0].automaticTitleVerified ? gifts[0] : null;
  return String(productId) === '12602893959486' ||
    Boolean(offer?.excludedProductIds?.includes(String(productId)));
}

/**
 * Remove an automatically added reward when no merchandise remains.
 * Its app marker distinguishes it from the same lamp bought intentionally.
 * The optional packaging service does not qualify as merchandise.
 */
async function removeOrphanedRewardLines(cart, updateCart) {
  const items = cart.items ?? [];
  const isReward = (item) => Boolean(item.properties?.__essential_cart_drawer_reward_tier_id);
  const rewards = items.filter(isReward);
  const hasMerchandise = items.some(
    (item) => item.quantity > 0 && !isReward(item) && !isExcludedFromRewardQualification(item.product_id)
  );

  if (!rewards.length || hasMerchandise) return cart;

  return updateCart(Object.fromEntries(rewards.map((item) => [item.key, 0])));
}

/**
 * Remove the last merchandise line and its automatic rewards in one request.
 * This prevents a reward-only intermediate cart from losing its free discount.
 * Missing row metadata falls back to the existing server-response cleanup.
 */
function getRewardRemovalUpdates(rows, lineId, quantity) {
  if (quantity !== 0 || rows.some((row) => !row.dataset.productId)) return null;

  const removed = new Set([lineId]);
  for (const row of rows) {
    if (removed.has(row.dataset.parentKey)) removed.add(row.dataset.key);
  }
  const rewards = rows.filter((row) => row.hasAttribute('data-mdc-cart-gift'));
  const hasRemainingMerchandise = rows.some((row) =>
    !removed.has(row.dataset.key) &&
    !row.hasAttribute('data-mdc-cart-gift') &&
    !isExcludedFromRewardQualification(row.dataset.productId)
  );
  const protectionEligibility = (row) => row.dataset.mdcProtectionEligible?.trim();
  const protectionEligibilityKnown = rows.every((row) => ['true', 'false'].includes(protectionEligibility(row)));
  const hasProtectionMerchandise = rows.some((row) =>
    !removed.has(row.dataset.key) && protectionEligibility(row) === 'true'
  );
  const protection = protectionEligibilityKnown && !hasProtectionMerchandise
    ? rows.filter((row) => String(row.dataset.productId) === '12602893959486')
    : [];
  const automaticRewards = !hasRemainingMerchandise || protection.length ? rewards : [];
  if (!automaticRewards.length && !protection.length) return null;

  return Object.fromEntries([[lineId, 0], ...automaticRewards.concat(protection).map((row) => [row.dataset.key, 0])]);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #pendingQuantityChanges = new Map();

  #debouncedOnChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    const row = this.refs.cartItemRows[line - 1];
    const key = row?.dataset.key;
    if (!key || row.classList.contains('removing')) return;

    clearTimeout(this.#pendingQuantityChanges.get(key));
    this.#pendingQuantityChanges.set(key, setTimeout(() => {
      this.#pendingQuantityChanges.delete(key);
      const currentLine = this.refs.cartItemRows.findIndex((item) => item.dataset.key === key) + 1;
      if (currentLine) this.#onQuantityChange({ quantity, line: currentLine });
    }, 300));
  };
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#pendingQuantityChanges.forEach((timeout) => clearTimeout(timeout));
    this.#pendingQuantityChanges.clear();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {{ quantity: number, line: number }} change - The pending change.
   */
  #onQuantityChange({ quantity, line }) {

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const key = row.dataset.key;
      clearTimeout(this.#pendingQuantityChanges.get(key));
      this.#pendingQuantityChanges.delete(key);
    });

    // Keep the current rows until Shopify confirms the mutation. Removing them
    // optimistically leaves a false empty cart if the request fails.
    return this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const lineId = this.refs.cartItemRows[config.line - 1]?.dataset.key;
    if (!lineId) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Return grouping data with the same confirmed mutation, within Shopify's five-section limit.
    if (sectionsToUpdate.size < 5) sectionsToUpdate.add('mdc-cart-savings-data');

    const rewardRemovalUpdates = getRewardRemovalUpdates(this.refs.cartItemRows, lineId, quantity);
    const requestUrl = rewardRemovalUpdates
      ? `${Theme.routes.cart_url}/update.js`
      : Theme.routes.cart_change_url;
    const body = JSON.stringify({
      ...(rewardRemovalUpdates ? { updates: rewardRemovalUpdates } : { id: lineId, quantity }),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    // Recovery is read-only: a timed-out POST may already have changed Shopify.
    const readCurrentCart = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.delete('section_id');
        sectionUrl.searchParams.set('sections', Array.from(sectionsToUpdate).join(','));
        const options = { credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json' } };
        const [cartResponse, sectionsResponse] = await Promise.all([
          fetch(`${Theme.routes.cart_url}.js`, options),
          // /cart negotiates JSON cart data for Accept: application/json, even with ?sections.
          fetch(sectionUrl.pathname + sectionUrl.search, { ...options, headers: { Accept: '*/*' } }),
        ]);
        if (!cartResponse.ok || !sectionsResponse.ok) throw new Error('Cart refresh unavailable');
        const [cart, sections] = await Promise.all([cartResponse.json(), sectionsResponse.json()]);
        if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) throw new Error('Invalid cart snapshot');
        // Recovery must not hide an unfinished automatic-reward cleanup.
        await removeOrphanedRewardLines(cart, async () => { throw new Error('Reward cleanup still required'); });
        return { ...cart, sections };
      } finally { clearTimeout(timeout); }
    };
    const applyCartResponse = async (parsedResponseText) => {
      if (typeof parsedResponseText.sections?.[this.sectionId] !== 'string' || !parsedResponseText.sections[this.sectionId].trim()) {
        throw new Error('Cart section unavailable');
      }
      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      this.#updateCartQuantitySelectorButtonStates();

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: parsedResponseText.sections,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });
    };

    const requestController = new AbortController();
    const requestTimeout = setTimeout(() => requestController.abort(), 20000);

    return fetch(requestUrl, { ...fetchConfig('json', { body }), signal: requestController.signal })
      .then(async (response) => {
        const responseText = await response.text();
        if (!response.ok) {
          // Shopify's validation errors still use the existing inline handler.
          try {
            if (JSON.parse(responseText).errors) return responseText;
          } catch { /* A non-JSON service failure must not render an empty cart. */ }
          throw new Error(`Cart update failed: HTTP ${response.status}`);
        }
        return responseText;
      })
      .then(async (responseText) => {
        let parsedResponseText = JSON.parse(responseText);

        if (!parsedResponseText.errors) {
          parsedResponseText = await removeOrphanedRewardLines(parsedResponseText, async (updates) => {
            const response = await fetch(
              `${Theme.routes.cart_url}/update.js`,
              {
                ...fetchConfig('json', {
                  body: JSON.stringify({
                    updates,
                    sections: Array.from(sectionsToUpdate).join(','),
                    sections_url: window.location.pathname,
                  }),
                }),
                signal: requestController.signal,
              }
            );
            const cart = await response.json();
            if (!response.ok || cart.errors) {
              throw new Error('Le cadeau n’a pas pu être retiré. Actualisez le panier avant le paiement.');
            }
            return cart;
          });
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // A different cart update can replace Shopify's line keys. Refresh, never replay the POST.
          let currentCart;
          try { currentCart = await readCurrentCart(); } catch { /* Keep the original validation error. */ }
          if (currentCart && !currentCart.items.some((item) => item.key === lineId)) {
            await applyCartResponse(currentCart);
            const language = document.documentElement.lang.split('-')[0];
            const messages = {
              fr: 'Votre panier a été actualisé. Vérifiez les quantités, puis réessayez votre modification.',
              en: 'Your cart has been refreshed. Check the quantities, then try your change again.',
              es: 'Tu carrito se ha actualizado. Revisa las cantidades y vuelve a intentar el cambio.',
              ar: 'تم تحديث سلتك. تحقّق من الكميات ثم حاول إجراء التغيير مرة أخرى.',
            };
            this.querySelector('[data-mdc-cart-refreshed]')?.remove();
            const notice = document.createElement('p');
            notice.setAttribute('data-mdc-cart-refreshed', '');
            notice.setAttribute('role', 'alert');
            notice.className = 'cart-item__error-text cart-primary-typography';
            notice.textContent = messages[language] || messages.fr;
            (this.querySelector('.cart-page__items') || this).append(notice);
            return;
          }
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        await applyCartResponse(parsedResponseText);
      })
      .catch(async (error) => {
        clearTimeout(requestTimeout);
        // Do not replay the quantity change: reconcile the real cart instead.
        try {
          const currentCart = await readCurrentCart();
          await applyCartResponse(currentCart);
          resetShimmer(this);
          return;
        } catch (refreshError) {
          console.warn('[cart-items] Refresh unavailable:', refreshError);
        }
        console.error(error);
        deferredUpdatePromise.reject(error);
        resetShimmer(this);

        const quantityInput = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
        if (quantityInput) quantityInput.value = quantityInput.defaultValue;
        const errorText = this.refs[`cartItemError-${line}`];
        const errorContainer = this.refs[`cartItemErrorContainer-${line}`];
        const language = document.documentElement.lang.split('-')[0];
        const messages = {
          fr: 'La mise à jour du panier a échoué. Actualisez le panier avant de réessayer.',
          en: 'The cart could not be updated. Refresh the cart before trying again.',
          es: 'No se ha podido actualizar el carrito. Actualízalo antes de volver a intentarlo.',
          ar: 'تعذّر تحديث السلة. حدّث السلة قبل المحاولة مجددًا.',
        };
        if (errorText) errorText.textContent = messages[language] || messages.fr;
        errorContainer?.classList.remove('hidden');

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        clearTimeout(requestTimeout);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}



